
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TeamAssignment } from "./TeamAssignment";
import { Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface AddClientFormProps {
  onSuccess: () => void;
  onClose: () => void;
}

interface Team {
  id: string;
  name: string;
  description?: string;
}

export const AddClientForm = ({ onSuccess, onClose }: AddClientFormProps) => {
  const { toast } = useToast();
  const [isAdding, setIsAdding] = useState(false);
  const [currentStep, setCurrentStep] = useState<"details" | "teams">("details");
  const [tempClientId, setTempClientId] = useState<string | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);
  const [clientCredentials, setClientCredentials] = useState<{ email: string; password: string } | null>(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobilePhone: "",
    companyName: "",
  });

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const { data, error } = await supabase
          .from('teams')
          .select('id, name, description')
          .order('name');
          
        if (error) {
          console.error('Error fetching teams:', error);
          return;
        }
        
        setTeams(data || []);
      } catch (error) {
        console.error('Error in fetchTeams:', error);
      }
    };
    
    fetchTeams();
  }, []);

  const handleInputChange = (field: string) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const generateCodeFromMobilePhone = (mobilePhone: string | undefined | null): string => {
    if (!mobilePhone) return Math.random().toString(36).slice(-4).toUpperCase();
    
    const cleanedPhone = mobilePhone.replace(/\D/g, '');
    const lastFourDigits = cleanedPhone.slice(-4).padStart(4, '0');
    const code = `C${lastFourDigits}#Client123`;
    
    return code;
  };

  const checkIfClientExists = async (email: string) => {
    console.log(`Checking if client with email ${email} exists...`);
    
    try {
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("id, email")
        .eq("email", email)
        .maybeSingle();
        
      if (profileError) {
        console.error("Error checking profiles:", profileError);
      }
      
      if (profileData) {
        console.log(`Found existing profile with email ${email}`);
        return true;
      }
      
      const { data: signupData, error: signupError } = await supabase
        .from("client_signups")
        .select("email")
        .eq("email", email)
        .maybeSingle();
        
      if (signupError) {
        console.error("Error checking client_signups:", signupError);
      }
      
      if (signupData) {
        console.log(`Found existing client_signup with email ${email}`);
        return true;
      }
      
      console.log(`No existing account found for email ${email}`);
      return false;
      
    } catch (error) {
      console.error("Error in checkIfClientExists:", error);
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!formData.firstName || !formData.lastName || !formData.email) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields (First Name, Last Name, and Email).",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsAdding(true);
      
      const clientExists = await checkIfClientExists(formData.email);
      if (clientExists) {
        toast({
          title: "Client already exists",
          description: `A client with the email ${formData.email} already exists in the system.`,
          variant: "destructive",
        });
        setIsAdding(false);
        return;
      }

      const code = generateCodeFromMobilePhone(formData.mobilePhone);
      
      const tempPassword = `${code}!2Ab`;

      console.log(`Generated client code: ${code}`);
      console.log(`Using password: ${tempPassword}`);
      console.log(`Mobile phone: ${formData.mobilePhone}`);
      console.log(`Selected team: ${selectedTeam}`);

      const { error: signupError } = await supabase
        .from("client_signups")
        .insert({
          email: formData.email,
          company_name: formData.companyName,
          contact_person: `${formData.firstName} ${formData.lastName}`,
          phone: formData.mobilePhone,
          status: "completed"
        });

      if (signupError) {
        console.error("Error adding to client_signups:", signupError);
        throw signupError;
      } else {
        console.log("Successfully added to client_signups table");
      }

      try {
        // Get current admin user ID to set as creator
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        
        if (!currentUser?.id) {
          throw new Error("Current user not found");
        }
        
        console.log(`Creating client with created_by = ${currentUser.id}`);
        
        // Use edge function to create user with admin privileges
        const { data: authData, error: authError } = await supabase.functions.invoke('create-client-user', {
          body: {
            email: formData.email,
            password: tempPassword,
            userData: {
              full_name: `${formData.firstName} ${formData.lastName}`,
              first_name: formData.firstName,
              last_name: formData.lastName,
              mobile_phone: formData.mobilePhone,
              company_name: formData.companyName,
              code: code,
              Role: "client",
              created_by: currentUser.id // Pass creator ID
            }
          }
        });

        if (authError) {
          console.error("Auth error:", authError);
          throw authError;
        }

        if (!authData?.user) {
          console.error("No user data returned");
          throw new Error("No user data returned");
        }

        const createdUser = authData.user;
        console.log("Auth user created with ID:", createdUser.id);

        // Wait a bit for the profile to be created by the Supabase trigger
        await new Promise(resolve => setTimeout(resolve, 2000));

        await updateUserProfile(createdUser.id);
        
        // Note: Client role is now assigned by the edge function using service role privileges

        if (selectedTeam) {
          await assignClientToTeam(createdUser.id, selectedTeam);
        }

        // Store credentials to display in the dialog
        setClientCredentials({
          email: formData.email,
          password: tempPassword
        });
        
        setTempClientId(authData.user.id);
        setCurrentStep("teams");
        setIsAdding(false); // Reset loading state after success

        try {
          console.log("Sending credentials email to client...");
          const { data, error } = await supabase.functions.invoke('send-credentials', {
            body: { 
              clientId: authData.user.id,
              password: tempPassword
            }
          });
          
          if (error) {
            console.error("Error sending credentials email:", error);
          } else {
            console.log("Credentials email sent successfully:", data);
          }
        } catch (emailError) {
          console.error("Error invoking send-credentials function:", emailError);
        }
      } catch (authError: any) {
        console.error("Auth error during client creation:", authError);
        
        toast({
          title: "Error Creating Account",
          description: authError.message || "There was a problem creating the auth account. The client data was saved, but the login account creation failed.",
          variant: "destructive",
        });
        setIsAdding(false);
      }
    } catch (error: any) {
      console.error("Error adding client:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to add client. Please try again.",
        variant: "destructive",
      });
      setIsAdding(false);
    }
  };

  const assignClientToTeam = async (clientId: string, teamId: string) => {
    try {
      const { error } = await supabase
        .from('client_teams')
        .insert({ client_id: clientId, team_id: teamId });
        
      if (error) {
        console.error("Error assigning client to team:", error);
        return false;
      }
      
      console.log(`Successfully assigned client ${clientId} to team ${teamId}`);
      return true;
    } catch (error) {
      console.error("Error in assignClientToTeam:", error);
      return false;
    }
  };

  const updateUserProfile = async (userId: string) => {
    // Get current user to set as created_by
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user?.id) {
      throw new Error("Current user not found");
    }
    
    console.log(`Updating profile ${userId} with created_by = ${user.id}`);
    
    const { data: updatedProfile, error: profileError } = await supabase
      .from("profiles")
      .update({ 
        mobile_phone: formData.mobilePhone,
        status: "active",
        Role: "client",
        first_name: formData.firstName,
        last_name: formData.lastName,
        full_name: `${formData.firstName} ${formData.lastName}`,
        code: generateCodeFromMobilePhone(formData.mobilePhone),
        email: formData.email,
        created_by: user.id // Set the current admin as creator
      })
      .eq("id", userId)
      .select()
      .maybeSingle();

    if (profileError) {
      console.error("Error updating profile:", profileError);
      throw profileError;
    }
    
    console.log("Profile updated successfully:", updatedProfile);
    
    // Verify created_by was set
    if (!updatedProfile?.created_by) {
      console.error("Warning: created_by was not set on profile");
    } else {
      console.log(`Verified: Profile created_by is ${updatedProfile.created_by}`);
    }
  };

  // Improved function to ensure client role is properly set
  const ensureClientRole = async (userId: string) => {
    try {
      console.log(`Ensuring client role for user ${userId}`);
      
      // First, check if user already has client role
      const { data: existingRoles, error: checkRoleError } = await supabase
        .from("user_roles")
        .select("*")
        .eq("user_id", userId)
        .eq("role", "client");
        
      if (checkRoleError) {
        console.error("Error checking existing roles:", checkRoleError);
        throw checkRoleError;
      }
      
      console.log("Existing client roles:", existingRoles);
      
      if (existingRoles && existingRoles.length > 0) {
        console.log("Client role already exists for user");
        return;
      }
      
      // If no client role exists, first remove any other roles (like guard)
      const { error: deleteError } = await supabase
        .from("user_roles")
        .delete()
        .eq("user_id", userId);
        
      if (deleteError) {
        console.error("Error removing existing roles:", deleteError);
      } else {
        console.log("Removed any existing roles for user");
      }
      
      // Now add the client role
      const { data: directRoleData, error: directRoleError } = await supabase
        .from("user_roles")
        .insert({ user_id: userId, role: "client" })
        .select();

      if (directRoleError) {
        console.error("Error in direct role insert:", directRoleError);
        
        if (directRoleError.code === '23505') {
          console.log("Role already existed - continuing");
        } else {
          throw directRoleError;
        }
      } else {
        console.log("Client role added successfully:", directRoleData);
      }
      
      // Verify the role was set correctly
      const { data: verifyRoles } = await supabase
        .from("user_roles")
        .select("*")
        .eq("user_id", userId);
        
      console.log("Final verification - user roles:", verifyRoles);
      
      return true;
    } catch (error) {
      console.error("Error ensuring client role:", error);
      throw error;
    }
  };

  const handleTeamsComplete = () => {
    onSuccess();
    // Don't close automatically - let user close manually after seeing credentials
  };

  const handleManualClose = () => {
    onSuccess();
    onClose();
  };

  return (
    <div className="space-y-4 mt-4">
      {currentStep === "details" ? (
        <>
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name *</Label>
            <Input
              id="firstName"
              placeholder="First Name"
              value={formData.firstName}
              onChange={handleInputChange("firstName")}
              disabled={isAdding}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name *</Label>
            <Input
              id="lastName"
              placeholder="Last Name"
              value={formData.lastName}
              onChange={handleInputChange("lastName")}
              disabled={isAdding}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleInputChange("email")}
              disabled={isAdding}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="mobilePhone">Mobile Phone</Label>
            <Input
              id="mobilePhone"
              placeholder="Mobile Phone"
              value={formData.mobilePhone}
              onChange={handleInputChange("mobilePhone")}
              disabled={isAdding}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="team">Team</Label>
            <Select 
              value={selectedTeam || undefined} 
              onValueChange={setSelectedTeam}
              disabled={isAdding}
            >
              <SelectTrigger id="team">
                <SelectValue placeholder="Select a team" />
              </SelectTrigger>
              <SelectContent className="max-h-60 overflow-y-auto">
                {teams.map(team => (
                  <SelectItem key={team.id} value={team.id}>
                    {team.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              You can assign more teams after creating the client
            </p>
          </div>

          <Button
            className="w-full mt-6"
            onClick={handleSubmit}
            disabled={isAdding}
          >
            {isAdding ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Adding...
              </>
            ) : (
              "Add Client & Continue to Teams"
            )}
          </Button>
        </>
      ) : (
        tempClientId && (
          <div className="space-y-6">
            {clientCredentials && (
              <div className="bg-amber-50 dark:bg-amber-950 border-2 border-amber-500 rounded-lg p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <div className="bg-amber-500 rounded-full p-1 mt-0.5">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/>
                      <line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-amber-900 dark:text-amber-100 mb-2">
                      Important: Save Client Credentials
                    </h4>
                    <p className="text-sm text-amber-800 dark:text-amber-200 mb-3">
                      Please copy and save these credentials before closing this dialog. The password will not be shown again.
                    </p>
                    <div className="bg-white dark:bg-gray-900 rounded-md p-3 space-y-2 font-mono text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Email:</span>
                        <div className="font-semibold text-gray-900 dark:text-gray-100 break-all">
                          {clientCredentials.email}
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Password:</span>
                        <div className="font-semibold text-gray-900 dark:text-gray-100 break-all">
                          {clientCredentials.password}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div>
              <h3 className="text-lg font-medium">Assign Teams to Client</h3>
              <p className="text-sm text-muted-foreground">
                Assign the client to one or more teams to enable access to schedules and services.
              </p>
            </div>
            
            <TeamAssignment 
              clientId={tempClientId} 
              onComplete={handleTeamsComplete}
            />
            
            <Button 
              onClick={handleManualClose}
              className="w-full"
              variant="default"
            >
              Close
            </Button>
          </div>
        )
      )}
    </div>
  );
};
